import { Button, UploadButton } from '@fl/cmsch-fe-library';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Field as ReduxFormField } from 'redux-form';
import { AssociationType } from 'api/gen/AssociationType';
import { Sex } from 'api/gen/Sex';
import { useOurTranslation } from 'app/translations';
import { Ant } from 'common/ant';
import { matingAction } from '../../model';
import { SaveCriteriasButton } from '../../utils';
const actionPanelRowGutter = 8;
interface Props {
  /** Sex-specific button labels and ids. */
  sex: Sex;
  /** Association type used by the filter actions. */
  associationType: AssociationType;
  /** Adds extra bottom spacing when true. */
  largerBottomSpacing?: boolean;
  /** Whether the filter form is valid. */
  valid: boolean;
  /** Whether the filter form is submitting. */
  submitting: boolean;
  /** Whether no criteria lists exist. */
  criteriaListsEmpty: boolean;
  /** Whether no animal lists exist. */
  animalListsEmpty: boolean;
  /** Whether the current animals page is empty. */
  isAnimalsPageEmpty: boolean;
  /** Opens or closes the saved animal lists modal. */
  toggleAnimalListsVisibility(): void;
  /** Opens or closes the add-animal modal. */
  toggleAddAnimalsVisibility(): void;
  /** Opens or closes the add-from-DB modal. */
  toggleAddAnimalsFromDBVisibility(): void;
  /** Opens or closes the overview modal for saved lists. */
  toggleListsOverviewVisibility(): void;
  /** Opens or closes the saved criteria lists modal. */
  toggleCriteriaListsVisibility(): void;
  /** Opens or closes the criteria-list-details modal. */
  toggleCriteriaListDetailsVisibility(): void;
}
const labelsBySex = {
  FEMALE: {
    addButtonLabelKey: 'addCows',
    addFromFileLabelKey: 'addCowsFromFile',
    addFromListLabelKey: 'addCowsFromList',
    addFromListTooltipKey: 'activeWithAtLeastOneCowList',
    saveListLabelKey: 'saveCowsList',
    saveListTooltipKey: 'activeWithAtLeastOneAddedCow',
    deleteLabelKey: 'deleteCows',
    deleteTooltipKey: 'activeWithAtLeastOneAddedCow',
    filterTestId: 'filterCows',
    addTestId: 'addCows',
    addFromFileTestId: 'addCowsFromFile',
    addFromListTestId: 'addCowsFromList',
    saveListTestId: 'saveCowsList',
    deleteTestId: 'deleteCows',
    manageSavedListsTestId: 'manageCowLists'
  },
  MALE: {
    addButtonLabelKey: 'addBulls',
    addFromFileLabelKey: 'addBullsFromFile',
    addFromListLabelKey: 'addBullsFromList',
    addFromListTooltipKey: 'activeWithAtLeastOneBullList',
    saveListLabelKey: 'saveBullList',
    saveListTooltipKey: 'activeWithAtLeastOneAddedBull',
    deleteLabelKey: 'deleteBulls',
    deleteTooltipKey: 'activeWithAtLeastOneAddedBull',
    filterTestId: 'filterBulls',
    addTestId: 'addBulls',
    addFromFileTestId: 'addBullsFromFile',
    addFromListTestId: 'addBullsFromList',
    saveListTestId: 'saveBullsList',
    deleteTestId: 'deleteBulls',
    manageSavedListsTestId: 'manageBullLists'
  }
} as const;

// eslint-disable-next-line max-lines-per-function
const BottomButtonsBase: FC<Props> = props => {
  const {
    sex,
    associationType,
    valid,
    submitting,
    criteriaListsEmpty,
    animalListsEmpty,
    isAnimalsPageEmpty,
    toggleAnimalListsVisibility,
    toggleAddAnimalsVisibility,
    toggleAddAnimalsFromDBVisibility,
    toggleListsOverviewVisibility,
    toggleCriteriaListsVisibility,
    toggleCriteriaListDetailsVisibility,
    largerBottomSpacing = false
  } = props;
  const dispatch = useDispatch();
  const {
    t
  } = useOurTranslation('mating');
  const labels = labelsBySex[sex];
  const handleCsvUpload = useCallback((file: File): void => {
    dispatch(matingAction.addAnimalsFromCsv(sex, file, associationType));
  }, [associationType, dispatch, sex]);
  const handleDeleteFromList = useCallback((silentMode?: boolean): void => {
    dispatch(matingAction.deleteAnimalsFromList(sex, associationType, silentMode));
  }, [associationType, dispatch, sex]);
  const handleDeleteButtonClick = useCallback((): void => {
    handleDeleteFromList();
  }, [handleDeleteFromList]);
  const filterButton = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <Button type="submit" visuals="primary" disabled={!valid || submitting} loading={submitting} tooltip={valid ? t('actionRewritesAnimalList') : t('formIsInvalid')} block testId={labels.filterTestId}>
                {t('filter')}
            </Button>
        </Ant.Col>, [labels.filterTestId, submitting, t, valid]);
  const addButton = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <Button onClick={toggleAddAnimalsVisibility} testId={labels.addTestId} block>{t(labels.addButtonLabelKey)}</Button>
        </Ant.Col>, [labels.addButtonLabelKey, labels.addTestId, toggleAddAnimalsVisibility, t]);
  const addFromFileButton = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <UploadButton id={labels.addFromFileTestId} title={t(labels.addFromFileLabelKey)} fileTypes=".csv" uploadFile={handleCsvUpload} block testId={labels.addFromFileTestId} />
        </Ant.Col>, [handleCsvUpload, labels.addFromFileLabelKey, labels.addFromFileTestId, t]);
  const addFromListButton = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <Button onClick={toggleAddAnimalsFromDBVisibility} disabled={animalListsEmpty} tooltip={animalListsEmpty ? t(labels.addFromListTooltipKey) : ''} block testId={labels.addFromListTestId}>
                {t(labels.addFromListLabelKey)}
            </Button>
        </Ant.Col>, [animalListsEmpty, labels.addFromListLabelKey, labels.addFromListTestId, labels.addFromListTooltipKey, toggleAddAnimalsFromDBVisibility, t]);
  const saveListButton = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <Button onClick={toggleAnimalListsVisibility} disabled={isAnimalsPageEmpty} tooltip={isAnimalsPageEmpty ? t(labels.saveListTooltipKey) : ''} block testId={labels.saveListTestId}>
                {t(labels.saveListLabelKey)}
            </Button>
        </Ant.Col>, [isAnimalsPageEmpty, labels.saveListLabelKey, labels.saveListTestId, labels.saveListTooltipKey, toggleAnimalListsVisibility, t]);
  const deleteButton = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <Button onClick={handleDeleteButtonClick} disabled={isAnimalsPageEmpty} tooltip={isAnimalsPageEmpty ? t(labels.deleteTooltipKey) : ''} block testId={labels.deleteTestId}>
                {t(labels.deleteLabelKey)}
            </Button>
        </Ant.Col>, [handleDeleteButtonClick, isAnimalsPageEmpty, labels.deleteLabelKey, labels.deleteTestId, labels.deleteTooltipKey, t]);
  const loadCriteriaFromList = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <Button onClick={toggleCriteriaListDetailsVisibility} disabled={criteriaListsEmpty} tooltip={criteriaListsEmpty ? t('activeWithAtLeastOneCriteriaList') : ''} block testId="loadCriteria">
                {t('loadCriteriaFromList')}
            </Button>
        </Ant.Col>, [criteriaListsEmpty, toggleCriteriaListDetailsVisibility, t]);
  const saveCriteriasButton = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <ReduxFormField name="criterias" component={SaveCriteriasButton} title={t('saveCriteriaList')} toggleCriteriaListsVisibility={toggleCriteriaListsVisibility} />
        </Ant.Col>, [toggleCriteriaListsVisibility, t]);
  const manageSavedLists = useMemo(() => <Ant.Col xs={24} md={8} xxl={4}>
            <Button onClick={toggleListsOverviewVisibility} block testId={labels.manageSavedListsTestId}>
                {t('manageSavedLists')}
            </Button>
        </Ant.Col>, [labels.manageSavedListsTestId, toggleListsOverviewVisibility, t]);
  return <Ant.Row justify="center" gutter={[actionPanelRowGutter, actionPanelRowGutter]} className={largerBottomSpacing ? 'mt-3 mb-5 mx-2' : 'mt-3 mb-3 mx-2'} data-sentry-element="unknown" data-sentry-component="BottomButtonsBase" data-sentry-source-file="bottom-buttons.tsx">
            {filterButton}
            {addButton}
            {addFromFileButton}
            {addFromListButton}
            {saveListButton}
            {deleteButton}
            {loadCriteriaFromList}
            {saveCriteriasButton}
            {manageSavedLists}
        </Ant.Row>;
};

/**
 * Memoized action bar rendered under the animal filter form.
 *
 * @param props Filter-state flags and modal toggle handlers.
 * @returns The bottom button group for the animal workflow.
 *
 * @example
 * <BottomButtons
 *   sex="FEMALE"
 *   associationType="HOLSTEIN"
 *   valid
 *   submitting={false}
 *   criteriaListsEmpty={false}
 *   animalListsEmpty={false}
 *   isAnimalsPageEmpty={false}
 *   toggleAnimalListsVisibility={noop}
 *   toggleAddAnimalsVisibility={noop}
 *   toggleAddAnimalsFromDBVisibility={noop}
 *   toggleListsOverviewVisibility={noop}
 *   toggleCriteriaListsVisibility={noop}
 *   toggleCriteriaListDetailsVisibility={noop}
 * />
 */
export const BottomButtons = memo(BottomButtonsBase);